import React, { useCallback, useMemo } from 'react';
import { useLocalSearchParams } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import Animated, { FadeIn } from 'react-native-reanimated';

import { AddToBag } from '@order/features-customization';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry, useTrackEventOnFocus } from '@order/Telemetry';

import { NotFoundView } from '../../components';
import { useLastInteractedStore } from '../../context';
import { useReorderNavigation } from '../../hooks/useReorderNavigation';
import { QuickAddCustomization, QuickAddModal, QuickAddPrompt } from './components';
import { useQuickAddNavigation, useQuickAddRemoteData } from './hooks';
import { QUICK_ADD_FADE_IN_DURATION } from './QuickAddScreen.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const QuickAddScreen = () => {
  const isQuickAddEnabled = useFeatureFlag('CELS-5657-order-quick-add-modifiable-product-enabled');

  if (!isQuickAddEnabled) {
    return (
      <QuickAddModal>
        <NotFoundView withoutFooter />
      </QuickAddModal>
    );
  }

  return <QuickAddScreenReady />;
};

// ─────────────────────────────────────────────────────────────────────────────

const QuickAddScreenReady = () => {
  const { baseProductSlug, sharedLineItemId } = useLocalSearchParams<RouteParams>();

  const lastInteractedStore = useLastInteractedStore();
  const { finishReorder } = useReorderNavigation();
  const { track } = useTelemetry();

  // ─── Derived ─────────────────────────────────────────────────────────

  const selectedRestaurantId = lastInteractedStore?.restaurantId;
  const selectedDeliveryAddressId = lastInteractedStore?.addressId;
  const selectedDeliveryFee = lastInteractedStore?.deliveryFee;
  const selectedVendorName = lastInteractedStore?.vendor;
  const selectedVendorRestaurantId = lastInteractedStore?.vendorRestaurantId;
  const hasLocation = Boolean(selectedRestaurantId);

  const vendor = useMemo(() => {
    if (!selectedVendorName || !selectedVendorRestaurantId) return;

    return { name: selectedVendorName, restaurantId: selectedVendorRestaurantId };
  }, [selectedVendorName, selectedVendorRestaurantId]);

  const { isFetchingProduct, isUnavailableProduct, productForRestaurant } = useQuickAddRemoteData({
    baseProductSlug,
    selectedDeliveryAddressId,
    selectedRestaurantId,
  });

  const { navigateToCustomization, navigateToLocationSearch } = useQuickAddNavigation({
    baseProductSlug,
    productSlug: productForRestaurant?.slug,
    restaurantSlug: productForRestaurant?.restaurant.slug,
    selectedDeliveryAddressId,
    sharedLineItemId,
  });

  const productName = productForRestaurant?.name;
  const locationName = productForRestaurant?.restaurant.name ?? DEFAULT_LOCATION_NAME;

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleAddToBagSuccess = useCallback(() => {
    track('quick-add.success', {
      baseProductSlug,
      sharedLineItemId,
      restaurantId: productForRestaurant?.restaurant.id,
    });

    if (selectedDeliveryAddressId) {
      finishReorder(undefined, selectedDeliveryAddressId);

      return;
    }

    finishReorder(productForRestaurant?.restaurant.slug);
  }, [
    baseProductSlug,
    finishReorder,
    productForRestaurant?.restaurant.id,
    productForRestaurant?.restaurant.slug,
    selectedDeliveryAddressId,
    sharedLineItemId,
    track,
  ]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventOnFocus({
    name: 'quick-add.viewed',
    payload: { baseProductSlug, sharedLineItemId },
  });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <QuickAddModal>
      <QuickAddContent
        baseProductSlug={baseProductSlug}
        hasLocation={hasLocation}
        isFetchingProduct={isFetchingProduct}
        isUnavailableProduct={isUnavailableProduct}
        locationName={locationName}
        productForRestaurant={productForRestaurant}
        productName={productName}
        selectedDeliveryAddressId={selectedDeliveryAddressId}
        selectedDeliveryFee={selectedDeliveryFee}
        sharedLineItemId={sharedLineItemId}
        vendor={vendor}
        onAddToBagSuccess={handleAddToBagSuccess}
        onCustomize={navigateToCustomization}
        onLocationSearch={navigateToLocationSearch}
      />
    </QuickAddModal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const QuickAddContent = (props: QuickAddContentProps) => {
  const {
    baseProductSlug,
    hasLocation,
    isFetchingProduct,
    isUnavailableProduct,
    locationName,
    productForRestaurant,
    productName,
    selectedDeliveryAddressId,
    selectedDeliveryFee,
    sharedLineItemId,
    vendor,
    onAddToBagSuccess,
    onCustomize,
    onLocationSearch,
  } = props;

  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  if (!hasLocation) {
    return (
      <QuickAddPrompt
        title={formatMessage(messages.selectLocationTitle)}
        description={formatMessage(messages.selectLocationMessage)}
        ctaLabel={formatMessage(messages.selectLocationCta)}
        onCtaPress={onLocationSearch}
      />
    );
  }

  if (isFetchingProduct) {
    return (
      <Animated.View entering={FadeIn.duration(QUICK_ADD_FADE_IN_DURATION)}>
        <AddToBag.Loading />
      </Animated.View>
    );
  }

  if (isUnavailableProduct || !productForRestaurant || !productName) {
    return (
      <QuickAddPrompt
        title={formatMessage(messages.unavailableProductTitle)}
        description={formatMessage(messages.unavailableProductMessage)}
        ctaLabel={formatMessage(messages.changeLocationCta)}
        onCtaPress={onLocationSearch}
      />
    );
  }

  return (
    <QuickAddCustomization
      baseProductSlug={baseProductSlug}
      productSlug={productForRestaurant.slug}
      productName={productName}
      restaurantSlug={productForRestaurant.restaurant.slug}
      locationName={locationName}
      sharedLineItemId={sharedLineItemId}
      addressId={selectedDeliveryAddressId}
      deliveryFee={selectedDeliveryFee}
      vendor={vendor}
      onAddToBagSuccess={onAddToBagSuccess}
      onCustomize={onCustomize}
    />
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_LOCATION_NAME = 'sweetgreen';

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  selectLocationTitle: {
    defaultMessage: 'Choose a location',
    description: 'Quick Add | Select location title',
  },
  selectLocationMessage: {
    defaultMessage: 'Select a location to add this item to your bag.',
    description: 'Quick Add | Select location message',
  },
  unavailableProductTitle: {
    defaultMessage: 'Item not available',
    description: 'Quick Add | Unavailable product title',
  },
  selectLocationCta: {
    defaultMessage: 'Select a location',
    description: 'Quick Add | Select location CTA',
  },
  unavailableProductMessage: {
    defaultMessage: 'This item is not available at your selected location.',
    description: 'Quick Add | Unavailable product message',
  },
  changeLocationCta: {
    defaultMessage: 'Change location',
    description: 'Quick Add | Change location CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type RouteParams = {
  baseProductSlug: string;
  sharedLineItemId?: string;
};

type QuickAddContentProps = {
  baseProductSlug: string;
  hasLocation: boolean;
  isFetchingProduct: boolean;
  isUnavailableProduct: boolean;
  locationName: string;
  productForRestaurant: ReturnType<typeof useQuickAddRemoteData>['productForRestaurant'];
  productName: string | undefined;
  selectedDeliveryAddressId: string | undefined;
  selectedDeliveryFee: number | undefined;
  sharedLineItemId: string | undefined;
  vendor: { name: string; restaurantId: string } | undefined;
  onAddToBagSuccess: () => void;
  onCustomize: () => void;
  onLocationSearch: () => void;
};
